import React, { useState, useMemo } from 'react';
import { Mafs, Coordinates, Polygon, Text, Plot, Line, Theme } from 'mafs';
import { Slider } from 'antd';

export const Histogram = ({ config, variables }) => {
    const [binWidth, setBinWidth] = useState(variables['binWidth'] || config.binWidth || 5);
    const [displayMode, setDisplayMode] = useState(config.displayMode || 'frequency');

    const data = config.data || [];

    const stats = useMemo(() => {
        if (!data.length) return null;
        const sorted = [...data].sort((a, b) => a - b);
        const n = data.length;

        const mean = data.reduce((sum, val) => sum + val, 0) / n;
        const median = n % 2 === 0 ? (sorted[n/2 - 1] + sorted[n/2]) / 2 : sorted[Math.floor(n/2)];
        const variance = data.reduce((sum, val) => sum + (val - mean) ** 2, 0) / n;
        const stdDev = Math.sqrt(variance);
        const q1 = sorted[Math.floor(n * 0.25)];
        const q3 = sorted[Math.floor(n * 0.75)];
        const iqr = q3 - q1;

        const lowerBound = q1 - 1.5 * iqr;
        const upperBound = q3 + 1.5 * iqr;
        const outliers = data.filter(d => d < lowerBound || d > upperBound);

        return { count: n, min: sorted[0], max: sorted[n - 1], mean, median, stdDev, variance, q1, q3, iqr, outliers };
    }, [data]);

    const { bins, xMin, xMax, maxY } = useMemo(() => {
        if (!data.length) return { bins: [], xMin: 0, xMax: 10, maxY: 10 };

        const minVal = Math.floor(Math.min(...data));
        const maxVal = Math.ceil(Math.max(...data));
        const safeBinWidth = Math.max(0.1, binWidth); // Prevent division by zero or negative infinite loops
        const totalBins = Math.ceil((maxVal - minVal) / safeBinWidth) + 1;

        const computedBins = [];
        let maxCount = 0;
        let modeCount = 0;
        let modeBin = null;

        for (let i = 0; i < totalBins; i++) {
            const start = minVal + (i * safeBinWidth);
            const end = start + safeBinWidth;
            const count = data.filter(d => d >= start && d < end).length;

            if (count > maxCount) maxCount = count;
            if (count > modeCount) {
                modeCount = count;
                modeBin = i;
            }

            let height = count;
            if (displayMode === 'density') {
                height = count / (data.length * safeBinWidth);
            }

            computedBins.push({
                x: start,
                count,
                height,
                isMode: i === modeBin,
                hasOutliers: stats?.outliers.some(o => o >= start && o < end) || false
            });
        }

        return {
            bins: computedBins,
            xMin: Math.min(0, minVal),
            xMax: maxVal + safeBinWidth,
            maxY: displayMode === 'density' ? Math.max(...computedBins.map(b => b.height)) * 1.2 : maxCount + 2
        };
    }, [data, binWidth, displayMode, stats]);

    const normalCurve = useMemo(() => {
        if (!config.overlay || config.overlay.type !== 'normal' || !stats) return null;
        const { mean, stdDev } = stats;

        const pdf = (x) => {
            const exponent = -((x - mean) ** 2) / (2 * stdDev ** 2);
            return (1 / (stdDev * Math.sqrt(2 * Math.PI))) * Math.exp(exponent);
        };

        const scale = displayMode === 'frequency' ? data.length * binWidth : 1;
        return (x) => pdf(x) * scale;
    }, [stats, config.overlay, displayMode, data.length, binWidth]);

    const renderBars = () => {
        return bins.map((bin, i) => {
            let color = config.barColor || Theme.indigo;
            let opacity = config.barOpacity || 0.6;

            if (bin.isMode && config.statistics?.showMode) color = Theme.green;
            if (bin.hasOutliers && config.highlightOutliers) color = Theme.red;

            return (
                <React.Fragment key={i}>
                    <Polygon
                        points={[[bin.x, 0], [bin.x + binWidth, 0], [bin.x + binWidth, bin.height], [bin.x, bin.height]]}
                        color={color}
                        fillOpacity={opacity}
                        strokeWidth={1}
                        strokeColor={color}
                    />
                    {config.showFrequencyLabels !== false && bin.count > 0 && (
                        <Text x={bin.x + binWidth / 2} y={bin.height + (maxY * 0.03)} size={14}>
                            {displayMode === 'frequency' ? bin.count : bin.height.toFixed(3)}
                        </Text>
                    )}
                </React.Fragment>
            );
        });
    };

    const renderStatistics = () => {
        if (!config.statistics || !stats) return null;
        return (
            <React.Fragment>
                {config.statistics.showMean && (
                    <>
                        <Line.Segment point1={[stats.mean, 0]} point2={[stats.mean, maxY]} color={Theme.green} weight={2} style="dashed" />
                        <Text x={stats.mean + 0.5} y={maxY * 0.9} color={Theme.green}>Mean: {stats.mean.toFixed(1)}</Text>
                    </>
                )}
                {config.statistics.showMedian && (
                    <>
                        <Line.Segment point1={[stats.median, 0]} point2={[stats.median, maxY]} color={Theme.orange} weight={2} style="dashed" />
                        <Text x={stats.median + 0.5} y={maxY * 0.8} color={Theme.orange}>Median: {stats.median.toFixed(1)}</Text>
                    </>
                )}
                {config.statistics.showStdDev && (
                    <Polygon
                        points={[
                            [stats.mean - stats.stdDev, 0], [stats.mean + stats.stdDev, 0],
                            [stats.mean + stats.stdDev, maxY], [stats.mean - stats.stdDev, maxY]
                        ]}
                        color={Theme.blue} fillOpacity={0.1} strokeWidth={0}
                    />
                )}
            </React.Fragment>
        );
    };

    const renderSummary = () => {
        if (!config.showSummary || !stats) return null;
        return (
            <div className="ch-histogram-summary" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                <h4 style={{ marginBottom: '15px' }}>Summary Statistics</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
                    <div className="ch-stat-group">
                        <h5 style={{ margin: '0 0 5px 0' }}>📊 Central Tendency</h5>
                        <div>Mean: {stats.mean.toFixed(2)}</div>
                        <div>Median: {stats.median.toFixed(2)}</div>
                        <div>Mode: Bin {bins.findIndex(b => b.isMode) + 1}</div>
                    </div>
                    <div className="ch-stat-group">
                        <h5 style={{ margin: '0 0 5px 0' }}>📏 Spread</h5>
                        <div>Std Dev: {stats.stdDev.toFixed(2)}</div>
                        <div>Variance: {stats.variance.toFixed(2)}</div>
                        <div>IQR: {stats.iqr.toFixed(2)}</div>
                    </div>
                    <div className="ch-stat-group">
                        <h5 style={{ margin: '0 0 5px 0' }}>📦 Quartiles</h5>
                        <div>Q1: {stats.q1.toFixed(2)}</div>
                        <div>Q2 (Median): {stats.median.toFixed(2)}</div>
                        <div>Q3: {stats.q3.toFixed(2)}</div>
                    </div>
                    <div className="ch-stat-group">
                        <h5 style={{ margin: '0 0 5px 0' }}>ℹ️ Basic Info</h5>
                        <div>Count: {stats.count} values</div>
                        <div>Range: [{stats.min}, {stats.max}]</div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="ch-histogram">
            <div className="ch-histogram-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                {config.binControl?.enabled && (
                    <div className="ch-bin-control" style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: '1', maxWidth: '300px' }}>
                        <label style={{ fontWeight: 'bold' }}>Bin Width:</label>
                        <Slider
                            style={{ flex: '1' }}
                            min={config.binControl.min || 1}
                            max={config.binControl.max || 20}
                            step={config.binControl.step || 1}
                            value={binWidth}
                            onChange={setBinWidth}
                        />
                        <span className="ch-bin-value">{binWidth}</span>
                    </div>
                )}

                <div className="ch-display-mode" style={{ display: 'flex', gap: '15px' }}>
                    <label style={{ cursor: 'pointer' }}>
                        <input
                            type="radio"
                            value="frequency"
                            checked={displayMode === 'frequency'}
                            onChange={(e) => setDisplayMode(e.target.value)}
                            style={{ marginRight: '5px' }}
                        />
                        Frequency
                    </label>
                    <label style={{ cursor: 'pointer' }}>
                        <input
                            type="radio"
                            value="density"
                            checked={displayMode === 'density'}
                            onChange={(e) => setDisplayMode(e.target.value)}
                            style={{ marginRight: '5px' }}
                        />
                        Density
                    </label>
                </div>
            </div>

            <div className="ch-histogram-content">
                <Mafs viewBox={{ x: [xMin, xMax], y: [0, maxY] }} height={400} pan={false}>
                    <Coordinates.Cartesian xAxis={{ labels: (x) => `${x}` }} yAxis={{ labels: (y) => `${y}` }} />
                    {renderBars()}
                    {renderStatistics()}
                    {normalCurve && (
                        <Plot.OfX y={normalCurve} color={config.overlay.color || Theme.red} style={config.overlay.style || "dashed"} weight={3} />
                    )}
                </Mafs>
                {renderSummary()}
            </div>
        </div>
    );
};